import { useEffect, useState } from 'react'
import { Box, Text, Menu, MenuButton, MenuList, Image,
  MenuItem, Button } from '@chakra-ui/react'
import { useDispatch } from 'react-redux'
import { FaUser, FaChevronDown } from 'react-icons/fa'
import { setToken } from 'store/modules/user'
import { clearPersistedState } from 'store/index'
import { useNavigate } from 'react-router-dom'
import { API_URL } from 'constants'
import { USER_TYPES } from 'constants'
import { theme } from 'styles'
import { SUPER_ADMIN_USERNAMES } from 'constants'
import PalomaLogo from 'asset/logo.jpg'
import { getStorageMe } from 'services/storage'

const Header = ({ user }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  // Non-admins only get the File Storage link once an admin has shared a
  // folder with them; admins always have it.
  const [hasFileAccess, setHasFileAccess] = useState(false)
  useEffect(() => {
    if (!user || user.user_type === USER_TYPES.ADMIN) { setHasFileAccess(false); return }
    let active = true
    getStorageMe().then(res => {
      if (active && res && res.status === 200) setHasFileAccess(Boolean(res.data.has_access))
    })
    return () => { active = false }
  }, [user && user._id, user && user.user_type]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box
      as="header"
      display="flex"
      alignItems="center"
      bg="white"
      px={{ base: 4, md: 8 }}
      py={3}
      position="sticky"
      top={0}
      zIndex={20}
      // A brand rule along the bottom rather than a plain grey hairline, so the
      // header carries some green without going back to a solid green bar.
      borderBottomWidth="3px"
      borderColor={theme.green}
      boxShadow="0 1px 3px rgba(17,24,21,.04)"
    >
      <Box
        as="button"
        type="button"
        display="flex"
        alignItems="center"
        gap={3}
        onClick={() => { window.location.href = '/' }}
        _focusVisible={{ outline: `2px solid ${theme.green}`, outlineOffset: '3px' }}
      >
        {/* The logo is already the wordmark, so no text lockup beside it.
            logo.jpg has a baked-in white background and the header is white,
            so it sits flush without needing a transparent asset. */}
        <Image src={PalomaLogo} alt="Paloma Model &amp; Talent" height="38px" />
      </Box>

      <Box ml="auto">
        {user && (
          <Menu>
            <MenuButton
              as={Button}
              bg="white"
              bgColor="white"
              borderWidth="1px"
              borderColor={theme.line}
              borderRadius="999px"
              px={4}
              py={2}
              height="auto"
              fontSize="13px"
              fontWeight="600"
              color={theme.ink}
              _hover={{ bg: theme.greenTint, bgColor: theme.greenTint, borderColor: theme.green }}
              _active={{ bg: theme.greenTint, bgColor: theme.greenTint }}
              rightIcon={<FaChevronDown size={11} />}
              leftIcon={user.logo ? (
                <Image
                  height="24px"
                  width="24px"
                  objectFit="cover"
                  borderRadius="999px"
                  src={`${API_URL}/s3/${encodeURIComponent(user.logo)}`}
                />
              ) : <FaUser size={13} />}
            >
              <Box as="span" display={{ base: 'none', md: 'inline' }}>
                {user.first_name} {user.last_name}
              </Box>
            </MenuButton>
            <MenuList
              color={theme.ink}
              borderColor={theme.line}
              borderRadius="12px"
              boxShadow="0 10px 30px rgba(17,24,21,.12)"
              py={2}
            >
              {user.user_type === USER_TYPES.ADMIN && (
                <MenuItem onClick={() => {
                  navigate('/enum-edit')
                }}>Edit Enum</MenuItem>
              )}
              {user.user_type === USER_TYPES.ADMIN && SUPER_ADMIN_USERNAMES.includes(user?.username) && (
                <MenuItem onClick={() => {
                  navigate('/admin-edit')
                }}>Edit Admins</MenuItem>
              )}
              {user.user_type === USER_TYPES.ADMIN && (
                <MenuItem onClick={() => {
                  navigate('/admin/dashboard/documents-manage')
                }}>Manage Documents</MenuItem>
              )}
              {(user.user_type === USER_TYPES.ADMIN || hasFileAccess) && (
                <MenuItem onClick={() => {
                  navigate('/files')
                }}>File Storage</MenuItem>
              )}
              {user.user_type !== USER_TYPES.TALENT && (
                <MenuItem onClick={() => {
                  navigate('/profile')
                }}>
                  <Box>
                    <Text fontSize="14px">{user.username}</Text>
                    <Text fontSize="12px" color={theme.muted}>{user.email}</Text>
                  </Box>
                </MenuItem>
              )}
              <MenuItem onClick={() => {
                navigate('/documents')
              }}>Documents</MenuItem>
              <MenuItem
                color={theme.danger}
                onClick={() => {
                  dispatch(setToken(null))
                  clearPersistedState()
                  navigate('/auth/login')
                }}
              >Logout</MenuItem>
            </MenuList>
          </Menu>
        )}
      </Box>
    </Box>
  )
}

export default Header
